import React, { useCallback, useState } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import { Tooltip } from "@mui/material";

import ItemLoadingCard from "~business/Cards/ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import Search from "~assets/icons/searchGris.png";

import useField from "~hooks/useField";
import ListView from "~styled/listView/ListView";

import Next from "@mui/icons-material/ArrowForward";

export type ListItem = { label: string; value: string | null };

type ChangePayload = {
  value: string;
  valid: boolean;
  focus: boolean;
};

export interface ItemSearchableInputCardProps {
  id: string;
  label: string;
  value: string;
  onChange?: (payload: ChangePayload) => void;
  onChangeItem?: (item: ListItem) => void;
  onClick?: () => void;
  placeholder?: string;
  tooltipContent?: React.ReactNode;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  items: ListItem[];
}

type LocalField = { value: string; valid: boolean; editing: boolean };
type UseFieldReturn = [
  LocalField,
  React.Dispatch<React.SetStateAction<LocalField>>,
  (next: boolean) => void,
  React.RefObject<HTMLInputElement>
];

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2%",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "18px",
    fontWeight: 500,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    maxWidth: pxToRem(270),
    width: "50%",
    position: "relative",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "5%",
    textAlign: "right",
    width: "100%",
    "& .Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
    "&:Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "grey",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: {
    width: 24,
    height: 24,
  },
  listView: {
    position: "absolute",
    top: "85%",
    width: "100%",
    zIndex: 2 as any,
  },
}));

const ItemSearchableInputCard: React.FC<ItemSearchableInputCardProps> = ({
  id,
  label,
  value,
  onChange,
  onChangeItem,
  onClick,
  placeholder,
  tooltipContent,
  required = false,
  displayError = false,
  focus = false,
  onBlur,
  readOnly = false,
  isLoading = false,
  items,
}) => {
  const classes = useStyles();
  const [listView, setListView] = useState<boolean>(false);

  const validateFunction = useCallback(handleValidate(required), [required]);

  const [localValue, setValue, handleEdit, reference] = useField(
    value,
    validateFunction,
    focus,
    isLoading,
    readOnly
  ) as UseFieldReturn;

  const handleBlur = () => onBlur?.();

  const handleChangeValue = (next: string) => {
    setValue((prev) => ({ ...prev, value: next }));
    onChange?.({
      value: next,
      valid: validateFunction(next),
      focus: false,
    });
  };

  const handleItem = (item: { label: string; value: string | null }) => {
    onChangeItem?.(item);
    setListView(false);
  };

  const showError = !localValue.valid && displayError;

  if (isLoading) return <ItemLoadingCard mainInput label={label} />;

  const listForView: ListItem[] = items.map((it) => ({
    label: it.label ?? "",
    value: it.value ?? null,
  }));

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {localValue.editing ? (
          <>
            <TextField
              id={id}
              style={{ width: "100%" }}
              autoComplete="nope"
              inputProps={{
                className: classes.input,
                autoComplete: "nope",
                autoCorrect: "off",
                spellCheck: "false",
                form: {
                  autoComplete: "nope",
                },
              }}
              onChange={(event) => handleChangeValue(event.target.value)}
              onBlur={handleBlur}
              value={localValue.value}
              variant="outlined"
              placeholder={placeholder}
              error={showError}
              inputRef={reference}
            />
            <Tooltip title={tooltipContent ?? ""}>
              <div
                onClick={() => setListView(true)}
                className={classes.buttonLink}
              >
                <div onClick={onClick}>
                  <img
                    style={{ width: 28, height: 28 }}
                    alt="Buscar"
                    src={Search}
                  />
                </div>
              </div>
            </Tooltip>
            {listView && (
              <div className={classes.listView}>
                <ListView
                  isLoading={isLoading}
                  onItemSelected={handleItem}
                  list={listForView}
                  IconMostUsed={Next}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {value ? (
              <Typography className={classes.value}>{value}</Typography>
            ) : (
              <div>&mdash;</div>
            )}
            {!readOnly && (
              <div
                className={classes.buttonLink}
                onClick={() => handleEdit(true)}
              >
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ItemSearchableInputCard;

const handleValidate =
  (required?: boolean) =>
  (val: string): boolean =>
    !required || !!val;
